import React, { useRef, useState } from "react";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { Grid, TextField } from "@mui/material";
import {
  DateCalendar,
  DateTimePicker,
  StaticDatePicker,
  StaticDateTimePicker,
  StaticTimePicker,
  TimeClock,
  TimePicker
} from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useEffect } from "react";
import {
  applyDateOperation,
  getDataAwareSourceName,
  getDataAwareSourceNames,
  parseDateByFormat
} from "./dateOperations";

export default function DateComponent({
  data,
  handledatechange,
  textValue,
  datavalue,
  datemod,
  datatextvalue,
  formaction,
  setTextValue,
  selectedComponentData
}) {
  const component = data?.component;
  const componentName = component?.sName;
  const dataAwareSourceNames = getDataAwareSourceNames(data?.data?.sDataAware);
  const dateSourceName = getDataAwareSourceName(data?.data?.sDateSource);
  const dateSourceFormat = String(
    data?.data?.sDateSourceFormat || data?.data?.sDateFormat || "YYYY-MM-DD"
  ).trim();
  const operationType = String(data?.data?.sOperationType ?? "").trim().toUpperCase();
  const operationField = String(data?.data?.sOperationField ?? "").trim();
  const sourceRecord = dataAwareSourceNames
    .map(sourceName => selectedComponentData?.[sourceName])
    .find(record =>
      record && Object.prototype.hasOwnProperty.call(record, operationField)
    );
  const dataAwareChangeKey = JSON.stringify(
    dataAwareSourceNames.map(sourceName => textValue?.[sourceName] ?? null)
  );
  const dateSourceValue = dateSourceName
    ? textValue?.[dateSourceName]
    : undefined;

  const dateformat = (date, sourceFormat = "YYYY-MM-DD", returnFormat) => {
    if (date) {
      const parseDate = (date, format) => {
        const parts = String(date)?.split("-");
        const formatParts = format?.toLowerCase()?.split("-");
        const dateObj = {};

        formatParts?.forEach((part, index) => {
          dateObj[part] = parseInt(parts[index], 10);
        });

        return new Date(dateObj.yyyy, dateObj.mm - 1, dateObj.dd);
      };

      const formatDate = (dateObj, format) => {
        const dd = String(dateObj.getDate()).padStart(2, "0");
        const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
        const yyyy = dateObj.getFullYear();

        switch (format) {
          case "dd-mm-yyyy":
            return `${dd}-${mm}-${yyyy}`;
          case "yyyy-dd-mm":
            return `${yyyy}-${dd}-${mm}`;
          case "yyyy-mm-dd":
            return `${yyyy}-${mm}-${dd}`;
          case "mm-dd-yyyy":
            return `${mm}-${dd}-${yyyy}`;
          default:
            return "Invalid date format pattern";
        }
      };

      // Convert sourceFormat and returnFormat to lowercase
      const parsedDate = parseDate(date, sourceFormat?.toLowerCase());

      if (isNaN(parsedDate)) {
        return "Invalid date";
      }

      return formatDate(parsedDate, returnFormat?.toLowerCase());
    } else {
      return "";
    }
  };

  const parseDateValue = (value, sourceFormat) => {
    if (!value) return null;

    const normalizedDate = dateformat(
      value,
      sourceFormat || "YYYY-MM-DD",
      "YYYY-MM-DD"
    );
    const parsedDate = dayjs(normalizedDate);

    return parsedDate.isValid() ? parsedDate : null;
  };

  const defaultDate = parseDateValue(
    component?.sDefaultValue,
    component?.sSourceFormat
  );
  const initialDate =
    parseDateValue(
      datavalue,
      component?.sSendFormat || component?.sSourceFormat
    ) || defaultDate;

  const [dateValue, setDatevalue] = useState(initialDate);
  const baselineDateRef = useRef(initialDate);
  const lastOperationResultRef = useRef(null);

  const handleDate = event => {
    if (!event || !event.isValid?.()) return;

    baselineDateRef.current = event;
    lastOperationResultRef.current = null;
    setDatevalue(event);

    const formattedDate = dateformat(
      event.format("YYYY-MM-DD"),
      "YYYY-MM-DD",
      component?.sSendFormat
    );
    handledatechange(formattedDate);
  };

  const handleDateBlur = event => {
    const displayedValue = String(event?.target?.value ?? "")
      .replace(/\u200e/g, "")
      .trim();
    const displayFormat = String(
      component?.options?.others1?.format ||
      component?.sSendFormat ||
      component?.sSourceFormat ||
      "YYYY-MM-DD"
    ).trim();
    const parsedDate = parseDateByFormat(displayedValue, displayFormat);

    if (!parsedDate) return;

    const formattedDate = dateformat(
      parsedDate.format("YYYY-MM-DD"),
      "YYYY-MM-DD",
      component?.sSendFormat
    );

    if (textValue?.[componentName] === formattedDate) return;

    handleDate(parsedDate);
  };

  useEffect(() => {
    if (
      !componentName ||
      (datavalue && datavalue !== "NaN-NaN-NaN") ||
      !component?.sDefaultValue
    ) {
      return;
    }

    const formattedDefault = dateformat(
      component.sDefaultValue,
      component?.sSourceFormat,
      component?.sSendFormat
    );

    setTextValue(previous => {
      if (previous?.[componentName]) return previous;

      return {
        ...previous,
        [componentName]: formattedDefault
      };
    });
  }, [
    componentName,
    datavalue,
    component?.sDefaultValue,
    component?.sSourceFormat,
    component?.sSendFormat,
    setTextValue
  ]);

  useEffect(() => {
    if (!datavalue || datavalue === "NaN-NaN-NaN") return;

    const parsedDate = parseDateValue(
      datavalue,
      component?.sSendFormat || component?.sSourceFormat
    );

    if (!parsedDate) return;

    setDatevalue(parsedDate);

    if (datavalue !== lastOperationResultRef.current) {
      baselineDateRef.current = parsedDate;
    }
  }, [datavalue, component?.sSendFormat, component?.sSourceFormat]);

  useEffect(() => {
    if (
      dataAwareSourceNames.length === 0 ||
      !operationType ||
      !operationField ||
      (dateSourceName &&
        (dateSourceValue === "" ||
          dateSourceValue === null ||
          dateSourceValue === undefined)) ||
      !sourceRecord ||
      !Object.prototype.hasOwnProperty.call(sourceRecord, operationField)
    ) {
      return;
    }

    const baselineDate = dateSourceName
      ? parseDateByFormat(dateSourceValue, dateSourceFormat)
      : baselineDateRef.current || defaultDate;

    if (!baselineDate) return;

    const calculatedDate = applyDateOperation(
      baselineDate,
      operationType,
      sourceRecord[operationField]
    );

    if (!calculatedDate) return;

    const formattedDate = dateformat(
      calculatedDate.format("YYYY-MM-DD"),
      "YYYY-MM-DD",
      component?.sSendFormat
    );

    lastOperationResultRef.current = formattedDate;
    setDatevalue(calculatedDate);
    setTextValue(previous => {
      if (previous?.[componentName] === formattedDate) return previous;

      return {
        ...previous,
        [componentName]: formattedDate
      };
    });
  }, [
    dataAwareChangeKey,
    dateSourceName,
    dateSourceValue,
    dateSourceFormat,
    operationType,
    operationField,
    sourceRecord,
    componentName,
    component?.sSendFormat,
    component?.sSourceFormat,
    component?.sDefaultValue,
    setTextValue
  ]);
  // console.log(data?.component?.sSendFormat =='YYYY-MM-DD','sSendFormat');

  // useEffect(() => {
  //   // console.log('clogDate',data?.component?.sName);

  //   // let elmData1 = document.querySelector('.css-nxo287-MuiInputBase-input-MuiOutlinedInput-input')
  //   // // alert(JSON.stringify(data))
  //   // elmData1.id = data?.component?.sName
  //   // let elmData2 = document.querySelector('.css-nxo287-MuiInputBase-input-MuiOutlinedInput-input')
  //   // // alert(JSON.stringify(data))
  //   // elmData2.id = data?.component?.sName

  // },[data?.component?.sName])
  // function formatDateForInputChange(inputDate, format) {
  //   const date = new Date(inputDate);

  //   if (isNaN(date)) {
  //     return "Invalid date";
  //   }

  //   const options = {
  //     year: "numeric",
  //     month: "2-digit",
  //     day: "2-digit"
  //   };

  //   const formattedDate = new Intl.DateTimeFormat("en-US", options).format(date);
  //   const [month, day, year] = formattedDate.split("/");

  //   // Make format case-insensitive
  //   const formatLower = format.toLowerCase();

  //   switch (formatLower) {
  //     case "mm-dd-yyyy":
  //       return `${month}-${day}-${year}`;
  //     case "dd-mm-yyyy":
  //       return `${day}-${month}-${year}`;
  //     case "yyyy/mm/dd":
  //       return `${year}/${month}/${day}`;
  //     case "yyyy-mm-dd":
  //       return `${year}-${month}-${day}`;
  //     case "mm/dd/yyyy":
  //       return `${month}/${day}/${year}`;
  //     case "dd/mm/yyyy":
  //       return `${day}/${month}/${year}`;

  //     case "yyyy-dd-mm":
  //       return `${year}-${day}-${month}`;

  //     case "month dd, yyyy":
  //       return new Intl.DateTimeFormat("en-US", {
  //         year: "numeric",
  //         month: "long",
  //         day: "numeric"
  //       }).format(date);
  //     case "dd month yyyy":
  //       return new Intl.DateTimeFormat("en-US", { year: "numeric", month: "long", day: "numeric" })
  //         .format(date)
  //         .replace(",", "")
  //         .split(" ")
  //         .reverse()
  //         .join(" ");
  //     case "short":
  //       return new Intl.DateTimeFormat("en-US").format(date); // Short date format
  //     case "long":
  //       return new Intl.DateTimeFormat("en-US", {
  //         weekday: "long",
  //         year: "numeric",
  //         month: "long",
  //         day: "numeric"
  //       }).format(date);
  //     default:
  //       return "Format not supported";
  //   }
  // }

  return (
    // <Grid container spacing={2}>
    <Grid item position={"relative"}>
        {/* {dateformat(
          data?.component?.sDefaultValue,
          data?.component?.sSourceFormat,
          data?.component?.sSendFormat
        )} */}
        {/* {data?.component?.sDefaultValue} */}
      {/* {data?.component?.sSourceFormat} */}
     {/* {data?.component?.options?.others1?.format} */}
      {/* {data?.component?.sSourceFormat} */}
      {/* <br /> */}

      {/* {JSON.stringify( dateformat(datavalue ,'YYYY-DD-MM', data?.component?.options?.others1?.format))} */}

      <div style={{ overflow: "hidden" }}>
        {/* {datavalue}--
        {dateformat(datavalue , data?.component?.options?.others1?.format)}
        { JSON.stringify(data?.component?.options?.others1?.format)} */}
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          {
            <DemoContainer
              components={[
                "DatePicker",
                "TimePicker",
                "StaticDatePicker",
                "DateCalendar",
                "DateCalendar",
                "StaticTimePicker",
                "TimeClock",
                "DateTimePicker",
                "StaticDateTimePicker"
              ]}
            >
              {data?.component?.options?.mode === "DATE" &&
              data?.component?.options?.submode === "DatePicker" ? (
                <>
                  {/* { data?.component?.sHelper} */}
                  {formaction == undefined || formaction != "EDIT" ? (
                    <>
                      {/* {data?.component?.sName} */}
                      <DatePicker
                        id={data?.component?.sName}
                        // value={datavalue}
                        name={data?.component?.sName}
                        {...data?.component?.options?.others1}
                        {...data?.component?.options?.others2}
                        {...data?.component?.options?.others3}
                        slotProps={{
                          textField: {
                            helperText: data?.component?.sHelper,
                            id: data?.component?.sName,
                            onBlur: handleDateBlur
                          }
                        }}
                        label={data?.component?.sLabel}
                        // defaultValue={pselectDate}
                        value={dayjs(dateValue)}
                        onChange={e => handleDate(e)}
                        //  format={data?.component?.sSendFormat}
                        {...data?.component?.options?.others1}
                        {...data?.component?.options?.others2}
                        {...data?.component?.options?.others3}
                        {...data?.component?.sProps}
                        // slots={{
                        //   textField: params => {

                        //     return (
                        //       <>
                        //           <TextField {...params} id={data?.component?.sName} />
                        //       </>
                        //     );
                        //   }
                        // }}
                      />
                    </>
                  ) : null}

                  {formaction === "EDIT" &&
                  datavalue &&
                  datemod === "FREEFORM" &&
                  datavalue !== undefined ? (
                    <>
                      <DatePicker
                        id={data?.component?.sName}
                        label={data?.component?.sLabel}
                        defaultValue={dayjs(datavalue)}
                        onChange={e => handleDate(e)}
                        slotProps={{
                          textField: {
                            helperText: data?.component?.sHelper,
                            id: data?.component?.sName,
                            onBlur: handleDateBlur
                          }
                        }}
                        slots={{
                          textField: params => <TextField {...params} id={data?.component?.sName} />
                        }}
                        {...data?.component?.options?.others1}
                        {...data?.component?.options?.others2}
                        {...data?.component?.options?.others3}
                        {...data?.component?.sProps}
                      />
                    </>
                  ) : formaction === "EDIT" &&
                    datavalue &&
                    datemod === "DEFAULT" &&
                    datavalue !== undefined ? (
                    <>
                      <DatePicker
                        id={data?.component?.sName}
                        label={data?.component?.sLabel}
                        defaultValue={dayjs(datavalue)}
                        onChange={e => handleDate(e)}
                        slotProps={{
                          textField: {
                            helperText: data?.component?.sHelper,
                            id: data?.component?.sName,
                            onBlur: handleDateBlur
                          }
                        }}
                        slots={{
                          textField: params => <TextField {...params} id={data?.component?.sName} />
                        }}
                        {...data?.component?.options?.others1}
                        {...data?.component?.options?.others2}
                        {...data?.component?.options?.others3}
                        {...data?.component?.sProps}
                      />
                    </>
                  ) : (
                    formaction === "EDIT" && (
                      // datatextvalue &&
                      // datatextvalue !== "checked" &&
                      <>
                        <DatePicker
                          id={data?.component?.sName}
                          name={data?.component?.sName}
                          {...data?.component?.options?.others1}
                          {...data?.component?.options?.others2}
                          {...data?.component?.options?.others3}
                          slotProps={{
                            textField: {
                              helperText: data?.component?.sHelper,
                              id: data?.component?.sName,
                              onBlur: handleDateBlur
                            }
                          }}
                          label={data?.component?.sLabel}
                          value={dayjs(dateValue)}
                          onChange={e => handleDate(e)}
                          // defaultValue={dayjs(datatextvalue)}
                          
                          // slots={{
                          //   textField: params => (
                          //     <TextField {...params} id={data?.component?.sName} />
                          //   )
                          // }}
                          {...data?.component?.options?.others1}
                          {...data?.component?.options?.others2}
                          {...data?.component?.options?.others3}
                          {...data?.component?.sProps}
                        />
                      </>
                    )
                  )}
                </>
              ) : data?.component?.options?.mode === "DATE" &&
                data?.component?.options?.submode === "StaticDatePicker" ? (
                <StaticDatePicker
                  id={data?.component?.sName}
                  label={data?.component?.sLabel}
                  defaultValue={data?.component?.sDefaultValue}
                  onChange={e => handleDate(e)}
                  slotProps={{
                    textField: {
                      helperText: data?.component?.sHelper,
                      id: data?.component?.sName
                    }
                  }}
                  {...data?.component?.options?.others1}
                  {...data?.component?.options?.others3}
                  {...data?.component?.options?.others2}
                  {...data?.component?.sProps}
                />
              ) : data?.component?.options?.mode === "DATE" &&
                data?.component?.options?.submode === "DateCalendar" ? (
                <DateCalendar
                  label={data?.component?.sLabel}
                  //   defaultValue={data?.component?.sDefaultValue}
                  slotProps={{
                    textField: {
                      helperText: data?.component?.sHelper
                    }
                  }}
                  onChange={e => handleDate(e)}
                  {...data?.component?.options?.others1}
                  {...data?.component?.options?.others3}
                  {...data?.component?.options?.others2}
                  {...data?.component?.sProps}
                />
              ) : data?.component?.options?.mode === "TIME" &&
                data?.component?.options?.submode === "TimePicker" ? (
                <TimePicker
                  label={data?.component?.sLabel}
                  //   defaultValue={dayjs(data?.component?.sDefaultValue)}
                  slotProps={{
                    textField: {
                      helperText: data?.component?.sHelper
                    }
                  }}
                  onChange={e => handleDate(e)}
                  {...data?.component?.options?.others2}
                  {...data?.component?.options?.others1}
                  {...data?.component?.options?.others3}
                  {...data?.component?.sProps}
                />
              ) : data?.component?.options?.mode === "TIME" &&
                data?.component?.options?.submode === "StaticTimePicker" ? (
                <StaticTimePicker
                  label={data?.component?.sLabel}
                  //   defaultValue={dayjs(data?.component?.sDefaultValue)}
                  slotProps={{
                    textField: {
                      helperText: data?.component?.sHelper
                    }
                  }}
                  onChange={e => handleDate(e)}
                  {...data?.component?.options?.others1}
                  {...data?.component?.options?.others3}
                  {...data?.component?.options?.others2}
                  {...data?.component?.sProps}
                />
              ) : data?.component?.options?.mode === "TIME" &&
                data?.component?.options?.submode === "TimeClock" ? (
                <TimeClock
                  label={data?.component?.sLabel}
                  {...data?.component?.options?.others1}
                  {...data?.component?.options?.others3}
                  {...data?.component?.options?.others2}
                  defaultValue={dayjs(data?.component?.sDefaultValue)}
                  onChange={e => handleDate(e)}
                  slotProps={{
                    textField: {
                      helperText: data?.component?.sHelper
                    }
                  }}
                  {...data?.component?.sProps}
                />
              ) : data?.component?.options?.mode === "DATETIME" &&
                data?.component?.options?.submode === "DateTimePicker" ? (
                <DateTimePicker
                  label={data?.component?.sLabel}
                  id={data?.component?.sName}
                  //   defaultValue={dayjs(data?.component?.sDefaultValue)}
                  slotProps={{
                    textField: {
                      helperText: data?.component?.sHelper
                    }
                  }}
                  onChange={e => handleDate(e)}
                  {...data?.component?.options?.others1}
                  {...data?.component?.options?.others3}
                  {...data?.component?.options?.others2}
                  {...data?.component?.sProps}
                />
              ) : data?.component?.options?.mode === "DATETIME" &&
                data?.component?.options?.submode === "StaticDateTimePicker" ? (
                <StaticDateTimePicker
                  label={data?.component?.sLabel}
                  defaultValue={dayjs(data?.component?.sDefaultValue)}
                  slotProps={{
                    textField: {
                      helperText: data?.component?.sHelper
                    }
                  }}
                  onChange={e => handleDate(e)}
                  {...data?.component?.options?.others1}
                  {...data?.component?.options?.others3}
                  {...data?.component?.options?.others2}
                  {...data?.component?.sProps}
                />
              ) : null}
            </DemoContainer>
          }
        </LocalizationProvider>
        {/* // </Grid> */}
      </div>
    </Grid>
  );
}
